// src/v2/components/ViewKey.jsx
//
// ViewKey — shared "Key" legend (PROJECTS-TIMELINE-UX-AND-STANDARD-KEY).
// Popover variant: pill chip (up to 6 colour dots + "Key") opening a
// hover/focus/touch, right-aligned, body-portalled panel. Inline variant: single
// row of swatches for card headers. Swatch kinds mirror chart marks.
//
// Placement contract for every surface: put the popover Key in the right
// toolbar group, immediately left of the primary + Add action, or at the far
// right when no Add action is available (no empty action slot). Where Compact
// exists, Compact then a thin divider precede Key. Popovers open below and
// right-aligned to their own chip, constrained to the viewport. This contract
// does not opt other surfaces into ViewKey or migrate their existing legends.

import React, { useId, useRef } from "react";
import { useKeyInteraction } from "./useKeyInteraction.js";
import { Popover } from "../../components/Popover.jsx";
import { T } from "../../app/tokens";

export const SWATCH_KINDS = ["bar", "barFade", "diamond", "dot", "tick", "ring", "cell", "box"];

export function KeySwatch({ swatch }) {
  const kind = swatch?.kind || "dot";
  const color = swatch?.color || T.mid;
  const base = { display: "inline-block", flexShrink: 0, boxSizing: "border-box" };
  let style;
  switch (kind) {
    case "box":
      style = { ...base, width: 10, height: 10, margin: "0 4px", border: `2px ${swatch.borderStyle || "solid"} ${color}`, background: T.card };
      break;
    case "cell":
      style = { ...base, width: 16, height: 12, borderRadius: 3, background: color, border: `1px ${swatch.borderStyle || "solid"} ${swatch.border || color}` };
      break;
    case "bar":
      style = { ...base, width: 18, height: 9, borderRadius: 5, background: color };
      break;
    case "barFade":
      style = { ...base, width: 18, height: 9, borderRadius: 5, background: `linear-gradient(to right, ${color} 60%, ${T.listTransparent})` };
      break;
    case "diamond":
      style = { ...base, width: 9, height: 9, margin: "0 5px", background: color, transform: "rotate(45deg)" };
      break;
    case "tick":
      style = { ...base, width: 2, height: 14, margin: "0 8px", borderRadius: 1, background: color };
      break;
    case "ring":
      style = { ...base, width: 10, height: 10, margin: "0 4px", borderRadius: "50%", border: `${swatch.borderWidth ?? 2}px solid ${color}`, background: T.card };
      break;
    default:
      style = { ...base, width: 8, height: 8, margin: "0 5px", borderRadius: "50%", background: color };
  }
  return (
    <span aria-hidden="true" data-testid={`viewkey-swatch-${kind}`} data-kind={kind} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 14 }}>
      <span style={style} />
    </span>
  );
}

function KeyItem({ item, compact }) {
  return (
    <div data-testid="viewkey-item" style={{ display: "flex", alignItems: compact ? "center" : "flex-start", gap: 8, minWidth: 0 }}>
      <span style={{ paddingTop: compact ? 0 : 2 }}><KeySwatch swatch={item.swatch} /></span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: compact ? "inline" : "block", fontSize: compact ? T.fsXs : T.fsSm, fontWeight: compact ? T.fwRegular : T.fwSemibold, color: compact ? T.mid : T.dark, lineHeight: 1.35, ...item.labelStyle }}>{item.label}</span>
        {item.description ? (
          <span style={{ display: compact ? "inline" : "block", fontSize: T.fsXs, color: T.mid, lineHeight: 1.4 }}>{compact ? " · " : ""}{item.description}</span>
        ) : null}
      </span>
    </div>
  );
}

function normalizeGroups(groups, items) {
  if (Array.isArray(groups) && groups.length) return groups;
  return [{ label: null, items: items || [] }];
}

function collectDots(groups) {
  const seen = [];
  for (const g of groups) for (const it of g.items || []) {
    const c = it?.swatch?.color;
    if (c && !seen.includes(c)) seen.push(c);
    if (seen.length >= 6) return seen;
  }
  return seen;
}

export function ViewKey({
  title = "How to read this",
  groups,
  items,
  footer,
  variant = "popover",
  testId = "viewkey",
  ariaLabel,
}) {
  const allGroups = normalizeGroups(groups, items);
  const titleId = useId();
  const panelId = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const interaction = useKeyInteraction(triggerRef, panelRef);
  const { open, close } = interaction;

  if (variant === "inline") {
    const flat = allGroups.flatMap((g) => g.items || []);
    return (
      <div data-testid={`${testId}-inline`} role="list" aria-label={ariaLabel || title} style={{ display: "flex", flexWrap: "nowrap", whiteSpace: "nowrap", overflowX: "auto", alignItems: "center", justifyContent: "flex-end", gap: "4px 14px" }}>
        {flat.map((it, i) => (
          <div role="listitem" key={`${it.label}-${i}`} style={{ flexShrink: 0 }}><KeyItem item={it} compact /></div>
        ))}
      </div>
    );
  }

  const dots = collectDots(allGroups);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`${testId}-trigger`}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        {...interaction.triggerProps}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px",
          borderRadius: 999, border: `1px solid ${open ? T.blueSoftBorder : T.border}`,
          background: open ? T.blueSoft : T.card, color: T.dark, cursor: "help",
          fontSize: T.fsSm, fontWeight: T.fwSemibold, fontFamily: "inherit",
        }}
      >
        {dots.length > 0 && (
          <span aria-hidden="true" data-testid={`${testId}-dots`} style={{ display: "inline-flex", gap: 3 }}>
            {dots.map((c) => (
              <span key={c} data-testid={`${testId}-dot`} style={{ width: 6, height: 6, borderRadius: "50%", background: c }} />
            ))}
          </span>
        )}
        Key
      </button>
      {open && (
        <Popover
          triggerRef={triggerRef}
          onClose={close}
          portalToBody
          variant="menu"
          horizontalViewportInset={8}
          role="presentation"
          style={{ boxShadow: T.shadowTimelineKeyPopover, border: "none", borderRadius: 12 }}
        >
          <span ref={panelRef} {...interaction.panelProps} style={{ display: "block" }}>
            <div
              data-viewkey-panel=""
              data-testid={`${testId}-panel`}
              role="dialog"
              id={panelId}
              aria-labelledby={titleId}
              tabIndex={-1}
              style={{ outline: "none", padding: "14px 16px", width: allGroups.some((g) => (g.columns || 1) > 1) ? 420 : 300, maxWidth: "100%", boxSizing: "border-box" }}
            >
              <div id={titleId} style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, marginBottom: 10 }}>{title}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {allGroups.map((g, gi) => (
                  <div key={g.label || gi} data-testid={`${testId}-group`}>
                    {g.label && (
                      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".06em", textTransform: "uppercase", color: T.light, marginBottom: 6 }}>{g.label}</div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${g.columns || 1}, minmax(0, 1fr))`, gap: "8px 16px" }}>
                      {(g.items || []).map((it, i) => <KeyItem key={`${it.label}-${i}`} item={it} />)}
                    </div>
                  </div>
                ))}
              </div>
              {footer && (
                <div data-testid={`${testId}-footer`} style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: T.fsXs, color: T.mid, lineHeight: 1.45 }}>{footer}</div>
              )}
            </div>
          </span>
        </Popover>
      )}
    </>
  );
}

export default ViewKey;
